import CommonModal from "@/components/ui/CommonModal";
import { Form } from "antd";
import type { EditInquiryFormValues } from "../type";
import { BasicInfoFormFields } from "./BasicInfoFormFields";
import { InquiryDetailFields } from "./InquiryDetailFields/InquiryDetailFields";
import { PurchaseRequestFormFields } from "./PurchaseRequestFormFields/PurchaseRequestFormFields";
import { SupplierFormFields } from "./SupplierFormFields/SupplierFormFields";
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from "@/components/ui/buttons/PrimaryButton";
import { notifySuccess, notifyWarning } from "@/utils/notification";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SrmVendorBiddingDto } from "@/types/apis";
import { saveVendorBidding, saveVendorBiddingDraft } from "@/apis/inquiry.api";
import { extractFormFieldError } from "@/utils/form.helper";
import { useInitFormValues } from "../hooks/useInitFormValues";
import {
  useVendorBiddingDetailQuery,
  useVendorBiddingDraftDetailQuery,
} from "../hooks/useVendorBiddingDetailQuery";
import { useModal } from "@/hooks/modals/useModal";
import { useTranslation } from "react-i18next";

export const EditInquiryModal = () => {
  const { props, open, closeModal } = useModal("INITIATE_INQUIRY");
  const { recordId, onSuccess } = props;
  const { t } = useTranslation("inquiryManagement");
  const [form] = Form.useForm<EditInquiryFormValues>();

  const recordDetail = useVendorBiddingDetailQuery(recordId);
  const draftDetail = useVendorBiddingDraftDetailQuery();

  const cleanup = () => {
    closeModal();
    form.resetFields();
  };
  const onSubmitSuccess = () => {
    notifySuccess(t("保存成功"));
    cleanup();
    onSuccess?.();
    queryClient.invalidateQueries({
      queryKey: ["vendorBiddingDraftDetail"],
    });
  };

  useInitFormValues(form, open, recordId ? recordDetail : draftDetail);

  const queryClient = useQueryClient();

  const saveDraftMutation = useMutation({
    mutationFn: (data: Partial<SrmVendorBiddingDto>) =>
      saveVendorBiddingDraft(data),
    onSuccess: onSubmitSuccess,
  });

  const saveMutation = useMutation({
    mutationFn: (data: SrmVendorBiddingDto) => saveVendorBidding(data),
    onSuccess: onSubmitSuccess,
  });

  const isSubmitLoading = saveDraftMutation.isPending || saveMutation.isPending;

  const onSubmit = async (type: "DRAFT" | "PUBLISH") => {
    const isPublish = type === "PUBLISH";

    try {
      if (isPublish) {
        await form.validateFields();
      }
    } catch (error) {
      return notifyWarning(extractFormFieldError(error));
    }

    const values = form.getFieldsValue();

    if (isPublish && !values.itemList?.length) {
      notifyWarning(t("请添加询价物料"));
      return;
    }

    if (
      isPublish &&
      values.itemList?.some(
        (item) => !item.materialNameCn && !item.materialNameEn,
      )
    ) {
      notifyWarning(t("请添加询价物料名称"));
      return;
    }

    if (isPublish && !values.supplierList?.length) {
      notifyWarning(t("请添加供应商"));
      return;
    }

    const params = {
      biddingType: values.biddingType,
      biddingName: values.biddingName,
      deadline: values.deadline?.format("YYYY-MM-DD HH:mm"),
      itemList: values.itemList,
      supplierList: values.supplierList?.map((id) => ({
        supplierId: id,
      })),
      purchaseRequestIds: values.purchaseRequestIds ?? null,
    };

    if (!isPublish) {
      saveDraftMutation.mutate(params);
    } else {
      saveMutation.mutate(params as SrmVendorBiddingDto);
    }
  };

  const disabled = !!recordId;

  return (
    <CommonModal
      open={open}
      onCancel={cleanup}
      title={t("新增询价")}
      width={1200}
      centered
      footer={
        <FormFooter
          onSubmit={onSubmit}
          readonly={!!recordId}
          onClose={cleanup}
          isSubmitLoading={isSubmitLoading}
        />
      }
    >
      <Form
        initialValues={{
          biddingType: "PRODUCTION_RAW_AUXILIARY",
        }}
        form={form}
        disabled={disabled}
      >
        {/* 询价基本信息 */}
        <BasicInfoFormFields />
        {/* 询价明细 */}
        <InquiryDetailFields disabled={disabled} />
        {/* 供应商选择 */}
        <SupplierFormFields disabled={disabled} />
        {/* 关联采购单 */}
        <PurchaseRequestFormFields disabled={disabled} />
      </Form>
    </CommonModal>
  );
};

type FormFooterProps = {
  isSubmitLoading: boolean;
  onSubmit: (type: "DRAFT" | "PUBLISH") => void;
  readonly: boolean;
  onClose: () => void;
};

function FormFooter({
  isSubmitLoading,
  onSubmit,
  readonly,
  onClose,
}: FormFooterProps) {
  const { t } = useTranslation("inquiryManagement");
  if (readonly) {
    return (
      <PrimaryOutlineButton onClick={onClose}>{t("关闭")}</PrimaryOutlineButton>
    );
  }

  return (
    <>
      <PrimaryButton
        onClick={() => onSubmit("DRAFT")}
        loading={isSubmitLoading}
      >
        {t("暂存")}
      </PrimaryButton>
      <PrimaryButton
        onClick={() => onSubmit("PUBLISH")}
        loading={isSubmitLoading}
      >
        {t("发送询价")}
      </PrimaryButton>
    </>
  );
}
